import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { BeakerIcon } from '@heroicons/react/24/outline';
import { ClipboardButton, ClipboardInput, ClipboardRoot } from '@/components/ui/clipboard';

interface Props {
  secret: string;
}

// One-shot handoff slot — matches the key read on /developers/docs. The docs
// page consumes and clears it on mount, so the secret never lingers in
// sessionStorage beyond the navigation that carries it.
const HANDOFF_KEY = 'ae:developers:pending-api-key';

// Shared reveal-once panel used by both the create and rotate flows. Once the
// dialog containing this closes, the plaintext is gone forever — only the
// hash is persisted on the server.
export default function ApiKeySecretReveal({ secret }: Props) {
  function openDocsWithKey() {
    try {
      window.sessionStorage.setItem(HANDOFF_KEY, secret);
    } catch {
      // sessionStorage write can fail in private modes; the docs page still
      // opens — the user can paste the key into Scalar's auth panel manually.
    }
    window.open('/developers/docs', '_blank', 'noopener,noreferrer');
  }

  return (
    <Stack gap={4}>
      <Text color='danger' fontSize='sm' fontWeight='medium'>
        This key will not be shown again. Copy it now and store it somewhere safe.
      </Text>
      <ClipboardRoot value={secret}>
        <HStack>
          <ClipboardInput readOnly fontFamily='mono' fontSize='sm' />
          <ClipboardButton />
        </HStack>
      </ClipboardRoot>
      <HStack justify='space-between' align='center'>
        <Text color='fg.muted' fontSize='xs' flex='1'>
          Treat this like a password. Anyone with this key can access your workspace data.
        </Text>
        <Button size='xs' variant='outline' onClick={openDocsWithKey}>
          <BeakerIcon width={14} height={14} />
          Try it in API docs
        </Button>
      </HStack>
    </Stack>
  );
}